/**
 * The auto-drop signal, read off the ENVELOPE (internalapi-envelope shape).
 *
 * A `promotion_rejected` create failure is the textbook expected business
 * failure: the server refused the promo (per-user check, window closed
 * between quote and pay, …) with member copy in `message` and the
 * fine-grained reason in `error.details.promotionCode`. The client's answer
 * is the auto-drop — clear the stored code (the key change requotes at list
 * price) and show the copy — which every purchase step drives through this one
 * reader.
 *
 * Lives beside the client rather than under a domain because it reads the
 * ENVELOPE, not a payload: subscriptions and bookings both refuse a code the
 * same way, and a second copy of this is a second place for the codes to drift.
 *
 * @module lib/apiClient/promoRejection
 */

import type { ApiErrorResult } from './types';

export interface PromotionRejection {
  /** The promotion service's member-facing copy. */
  message: string;
  /** `details.promotionCode` — the fine-grained refusal reason, when present. */
  promotionCode?: string;
  /**
   * The buyer can fix this by changing the QUANTITY — the code is fine, the
   * order is the wrong size for it. Callers must NOT auto-drop the
   * code on these: dropping it takes away a discount the buyer is one click from.
   */
  quantityFixable: boolean;
}

/**
 * Compared as plain strings with a default of "not fixable": the refusal reason
 * is an OPEN wire union (`${PromotionErrorCodes}`), and a reason this build has
 * never heard of must fall to the safe, existing behaviour — auto-drop.
 */
const QUANTITY_FIXABLE_REASONS: readonly string[] = ['item_limit_exceeded', 'item_minimum_not_met'];

/** Null unless the failure envelope is a promo refusal. */
export function getPromotionRejection(result: ApiErrorResult): PromotionRejection | null {
  if (result.error?.code !== 'promotion_rejected') return null;
  const details = result.error.details as { promotionCode?: unknown } | null | undefined;
  const promotionCode = typeof details?.promotionCode === 'string' ? details.promotionCode : undefined;
  return {
    message: result.message,
    ...(promotionCode !== undefined && { promotionCode }),
    quantityFixable: promotionCode !== undefined && QUANTITY_FIXABLE_REASONS.includes(promotionCode),
  };
}
